import {useAuthService} from "~/composables/useAuthService";
import {useHttpRequest} from "~/composables/useHttpRequest";
import {endpoints} from "~/network/endpoints";
import {useRuntimeConfig} from "#app";

/**
 * Help chatbot ("assistant") client. POST /api/v2/assistant/chat streams SSE
 * (start → delta* → done); history is held server-side, so we only ever send
 * {conversationId, message}. Limits come back as 429 with a machine-readable
 * code: dailyLimit | spendCap | conversationFull | concurrentStream.
 *
 * The code only picks the AFFORDANCE — the wording always comes from the server,
 * so a limit added server-side (spendCap was) explains itself with no web release.
 */
// Registered once per page load — see the watch below.
let watchingUser = false;

export function useAssistant() {
  const config = useRuntimeConfig().public.grantThomasNuxt;
  const {accessToken, impersonatingAccessToken, user} = useAuthService();
  const {httpRequest} = useHttpRequest();

  const messages = useState('assistantMessages', () => []);
  const conversationId = useState('assistantConversationId', () => null);
  const streaming = useState('assistantStreaming', () => false);
  const limitNotice = useState('assistantLimitNotice', () => null);
  const turnsRemaining = useState('assistantTurnsRemaining', () => null);
  const hydrated = useState('assistantHydrated', () => false);
  // Monitoring/data-use notice — served by the API (never hardcoded) so legal
  // copy can change without an app release. Sending is gated on having it, so a
  // failed load means no message can be sent rather than a chat with no notice.
  const notice = useState('assistantNotice', () => null);

  // Which user the cached conversation belongs to.
  //
  // Everything above is an in-memory cache of a conversation the SERVER holds,
  // and useState survives sign-out within a tab — so without this the next
  // person to open the chat is shown the previous person's messages. Nothing is
  // persisted to the device; the leak is entirely this cache outliving its owner.
  const stateUserId = useState('assistantStateUserId', () => null);

  const clearForUser = (id) => {
    messages.value = [];
    conversationId.value = null;
    streaming.value = false;
    limitNotice.value = null;
    turnsRemaining.value = null;
    notice.value = null;
    hydrated.value = false;
    stateUserId.value = id;
  };

  const currentUserId = () => user.value?.id ?? null;

  // Checked synchronously on every call, BEFORE hydrate below, so a stale
  // conversation can never be rendered even for a frame. Signing out counts:
  // the id becomes null and the cache is dropped.
  if (import.meta.client && stateUserId.value !== currentUserId()) {
    clearForUser(currentUserId());
  }

  // The check above only runs when something calls the composable again. This
  // catches the account changing under a chat that is already on screen.
  if (import.meta.client && !watchingUser) {
    watchingUser = true;
    watch(currentUserId, (id) => {
      if (id !== stateUserId.value) clearForUser(id);
    });
  }

  // ---- resume-current: the server holds the conversation, so the chat comes
  // back across reloads and devices until New chat ----
  const hydrate = async ({force = false} = {}) => {
    if (!import.meta.client || !user.value?.id) {
      return;
    }
    if (!force && (hydrated.value || messages.value.length > 0)) {
      return;
    }
    hydrated.value = true;
    try {
      const result = await httpRequest(endpoints.assistant.currentConversation);
      notice.value = result?.notice ?? null;
      const conversation = result?.conversation;
      if (conversation && messages.value.length === 0) {
        conversationId.value = conversation.id;
        turnsRemaining.value = conversation.turnsRemaining;
        messages.value = conversation.messages.map(m => ({
          id: m.id,
          role: m.role,
          text: m.content,
          rating: m.rating ?? null,
          noAnswer: m.noAnswer ?? false,
        }));
      }
    } catch (e) {
      hydrated.value = false; // transient failure — retry on the next visit
    }
  };
  hydrate();

  const bearerToken = () => {
    if (impersonatingAccessToken.value != null && impersonatingAccessToken.value.length > 10) {
      return impersonatingAccessToken.value;
    }
    return accessToken.value;
  };

  const newConversation = () => {
    if (streaming.value) {
      return;
    }
    if (conversationId.value) {
      // Fire-and-forget: server-side close so the chat stays cleared everywhere
      httpRequest(endpoints.assistant.closeConversation, {}).catch(() => {});
    }
    conversationId.value = null;
    messages.value = [];
    limitNotice.value = null;
    turnsRemaining.value = null;
  };

  const sendMessage = async (text) => {
    text = (text || '').trim();
    // No notice on screen → nothing gets sent, so nothing is collected without
    // the disclosure having been shown.
    if (!text || streaming.value || !notice.value) {
      return;
    }

    limitNotice.value = null;
    streaming.value = true;

    const userMessage = {id: null, role: 'user', text};
    const assistantMessage = {id: null, role: 'assistant', text: '', rating: null, noAnswer: false, pending: true, error: null};
    messages.value = [...messages.value, userMessage, assistantMessage];

    try {
      const response = await fetch(`${config.apiBaseUrl}/api/v2/assistant/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
          Authorization: 'Bearer ' + bearerToken(),
        },
        body: JSON.stringify({conversationId: conversationId.value, message: text}),
      });

      if (!response.ok) {
        let body = null;
        try {
          body = await response.json();
        } catch (e) { /* non-JSON error body */ }
        if (response.status === 429) {
          // Every 429 is a limit, whether or not we recognise the code — an
          // unrecognised (or missing) one must still surface the server's own
          // explanation rather than a generic "request failed".
          limitNotice.value = {
            code: body?.code || 'unknown',
            message: body?.global?.[0] ||
                'The assistant is unavailable right now. Please try again later.',
          };
          // The message was refused, so no reply is coming. Drop the
          // placeholder rather than leave an empty bubble with the same
          // sentence under it and again in the notice above.
          messages.value = messages.value.filter(m => m !== assistantMessage);
          messages.value = [...messages.value];
          return;
        } else {
          assistantMessage.error = body?.global?.[0] || `Request failed (${response.status}).`;
        }
        assistantMessage.pending = false;
        messages.value = [...messages.value];
        return;
      }

      await readSseStream(response, {
        start: (data) => {
          conversationId.value = data.conversationId;
          userMessage.id = data.userMessageId;
          assistantMessage.id = data.assistantMessageId;
        },
        delta: (data) => {
          assistantMessage.text += data.text;
          messages.value = [...messages.value];
        },
        done: (data) => {
          assistantMessage.noAnswer = data.noAnswer;
          turnsRemaining.value = data.turnsRemaining;
        },
        error: (data) => {
          assistantMessage.error = data.message || 'The assistant hit a problem. Please try again.';
        },
      });
    } catch (e) {
      assistantMessage.error = 'The assistant is unavailable right now. Please try again.';
    } finally {
      assistantMessage.pending = false;
      messages.value = [...messages.value];
      streaming.value = false;
    }
  };

  const readSseStream = async (response, handlers) => {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const {done, value} = await reader.read();
      if (done) {
        break;
      }
      buffer += decoder.decode(value, {stream: true});

      let boundary;
      while ((boundary = buffer.indexOf('\n\n')) >= 0) {
        const frame = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);

        let event = 'message';
        let data = null;
        for (const line of frame.split('\n')) {
          if (line.startsWith('event: ')) {
            event = line.slice(7);
          } else if (line.startsWith('data: ')) {
            data = JSON.parse(line.slice(6));
          }
        }
        if (data && handlers[event]) {
          handlers[event](data);
        }
      }
    }
  };

  const rateMessage = async (message, rating) => {
    if (!message.id) {
      return;
    }
    // Tapping the active thumb again clears the rating
    const newRating = message.rating === rating ? null : rating;
    const previous = message.rating ?? null;
    // Optimistic: reflect the thumb immediately so it responds without waiting on
    // the request (which resolves after the round-trip, and can even reject on the
    // empty response body while the rating still persisted server-side).
    message.rating = newRating;
    messages.value = [...messages.value];
    try {
      await httpRequest(endpoints.assistant.rateMessage(message.id), {rating: newRating});
    } catch (err) {
      // Only revert on a genuine server rejection. httpRequest also rejects when it
      // fails to JSON-parse a successful empty body — that's not a real failure.
      const realFailure = !!(err && (typeof err.json === 'function' || err.status || err.code || err.global));
      if (realFailure) {
        message.rating = previous;
        messages.value = [...messages.value];
      }
    }
  };

  return {
    messages,
    conversationId,
    streaming,
    limitNotice,
    turnsRemaining,
    notice,
    retryNotice: () => hydrate({force: true}),
    sendMessage,
    rateMessage,
    newConversation,
  };
}
